import { useCallback, useEffect, useState } from "react";
import {
    createAdminEmployee,
    listAdminEmployees,
    setAdminEmployeeAccountStatus,
    updateAdminEmployee
} from "@/api/admin.api.js";
import { useAuth } from "@/auth/AuthContext.jsx";
import BackLink from "@/components/BackLink.jsx";
import ConfirmDialog from "@/components/ConfirmDialog.jsx";
import RowActionsMenu from "@/components/RowActionsMenu.jsx";
import { ROLES } from "@/utils/constants.js";
import { formatAccountStatus } from "@/utils/customerProfile.js";
import { canMutateEmployees, STAFF_ROLE_LABELS } from "@/utils/staffAccess.js";

const emptyForm = {
    username: "",
    password: "",
    firstName: "",
    lastName: "",
    email: "",
    role: ROLES.MANAGER,
    accountStatus: "ACTIVE"
};

function toForm(employee) {
    return {
        username: employee.username || "",
        password: "",
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        email: employee.email || "",
        role: employee.role || ROLES.MANAGER,
        accountStatus: employee.accountStatus || "ACTIVE"
    };
}

function displayName(employee) {
    return `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || employee.username;
}

export default function AdminEmployeesSection({ setMessage, setError }) {
    const { role: currentRole } = useAuth();
    const canMutate = canMutateEmployees(currentRole);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState("list");
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const response = await listAdminEmployees();
            setEmployees(response.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [setError]);

    useEffect(() => {
        load();
    }, [load]);

    const openCreate = () => {
        setSelected(null);
        setForm(emptyForm);
        setViewMode("form");
        setMessage("");
        setError("");
    };

    const openDetail = (employee) => {
        setSelected(employee);
        setForm(toForm(employee));
        setViewMode("detail");
        setMessage("");
        setError("");
    };

    const openEdit = (employee = selected) => {
        if (!employee?.editable) {
            return;
        }
        setSelected(employee);
        setForm(toForm(employee));
        setViewMode("form");
        setMessage("");
        setError("");
    };

    const backToList = async () => {
        setViewMode("list");
        setSelected(null);
        setForm(emptyForm);
        await load();
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!canMutate) {
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (selected) {
                const payload = {
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim() || undefined,
                    role: form.role,
                    accountStatus: form.accountStatus
                };
                if (form.password.trim()) {
                    payload.password = form.password.trim();
                }
                const response = await updateAdminEmployee(selected.username, payload);
                setMessage("Employé mis à jour.");
                setSelected(response.data);
                setForm(toForm(response.data));
                setViewMode("detail");
            } else {
                const response = await createAdminEmployee({
                    username: form.username.trim(),
                    password: form.password.trim(),
                    firstName: form.firstName.trim(),
                    lastName: form.lastName.trim(),
                    email: form.email.trim() || undefined,
                    role: form.role
                });
                setMessage("Employé créé.");
                setSelected(response.data);
                setForm(toForm(response.data));
                setViewMode("detail");
            }
            await load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleStatusChange = async () => {
        if (!pendingStatus) {
            return;
        }
        try {
            const response = await setAdminEmployeeAccountStatus(
                pendingStatus.username,
                pendingStatus.accountStatus
            );
            setMessage(
                pendingStatus.accountStatus === "ACTIVE"
                    ? "Compte activé."
                    : "Compte désactivé."
            );
            setPendingStatus(null);
            if (selected?.username === response.data.username) {
                setSelected(response.data);
                setForm(toForm(response.data));
            }
            await load();
        } catch (err) {
            setError(err.message);
            setPendingStatus(null);
        }
    };

    const buildMenuItems = (employee, { includeView = true } = {}) => {
        if (!employee) {
            return [];
        }
        const items = [];
        if (includeView) {
            items.push({
                id: "view",
                label: "Voir les détails",
                onClick: () => openDetail(employee)
            });
        }
        if (canMutate && employee.editable) {
            items.push({
                id: "edit",
                label: "Modifier",
                onClick: () => openEdit(employee)
            });
            if (employee.accountStatus === "ACTIVE") {
                items.push({
                    id: "deactivate",
                    label: "Désactiver",
                    danger: true,
                    onClick: () => setPendingStatus({
                        username: employee.username,
                        accountStatus: "DEACTIVATED",
                        label: displayName(employee)
                    })
                });
            } else if (employee.accountStatus === "DEACTIVATED" || employee.accountStatus === "CREATED") {
                items.push({
                    id: "activate",
                    label: "Activer",
                    onClick: () => setPendingStatus({
                        username: employee.username,
                        accountStatus: "ACTIVE",
                        label: displayName(employee)
                    })
                });
            }
        }
        return items;
    };

    const statusDialog = (
        <ConfirmDialog
            open={!!pendingStatus}
            title={pendingStatus?.accountStatus === "ACTIVE" ? "Activer le compte ?" : "Désactiver le compte ?"}
            message={
                pendingStatus
                    ? pendingStatus.accountStatus === "ACTIVE"
                        ? `Réactiver le compte de « ${pendingStatus.label} » ?`
                        : `Désactiver le compte de « ${pendingStatus.label} » ? La personne ne pourra plus se connecter.`
                    : ""
            }
            confirmLabel={pendingStatus?.accountStatus === "ACTIVE" ? "Activer" : "Désactiver"}
            onConfirm={handleStatusChange}
            onCancel={() => setPendingStatus(null)}
        />
    );

    if (viewMode === "form") {
        const isCreate = !selected;
        return (
            <section className="card profile-section">
                <div className="section-header">
                    <div>
                        <BackLink
                            onClick={() => (selected ? setViewMode("detail") : backToList())}
                            className="purchase-detail-back"
                        >
                            {selected ? "Retour au détail" : "Retour à la liste"}
                        </BackLink>
                        <h2>{isCreate ? "Nouvel employé" : "Modifier l'employé"}</h2>
                    </div>
                </div>
                <form className="profile-form" onSubmit={handleSave}>
                    {isCreate && (
                        <label className="field">
                            Nom d&apos;utilisateur
                            <input
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value })}
                                required
                                minLength={3}
                                maxLength={50}
                                autoComplete="off"
                            />
                        </label>
                    )}
                    <label className="field">
                        {isCreate ? "Mot de passe" : "Nouveau mot de passe (optionnel)"}
                        <input
                            type="password"
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            required={isCreate}
                            minLength={3}
                            maxLength={50}
                            autoComplete="new-password"
                        />
                    </label>
                    <label className="field">
                        Prénom
                        <input
                            value={form.firstName}
                            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                            required
                            maxLength={100}
                        />
                    </label>
                    <label className="field">
                        Nom
                        <input
                            value={form.lastName}
                            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                            required
                            maxLength={100}
                        />
                    </label>
                    <label className="field">
                        E-mail
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm({ ...form, email: e.target.value })}
                            maxLength={255}
                        />
                    </label>
                    <label className="field">
                        Rôle
                        <select
                            value={form.role}
                            onChange={(e) => setForm({ ...form, role: e.target.value })}
                            required
                        >
                            <option value={ROLES.MANAGER}>{STAFF_ROLE_LABELS[ROLES.MANAGER]}</option>
                            <option value={ROLES.SECRETARY}>{STAFF_ROLE_LABELS[ROLES.SECRETARY]}</option>
                        </select>
                    </label>
                    {!isCreate && (
                        <label className="field">
                            Statut du compte
                            <select
                                value={form.accountStatus === "CREATED" ? "ACTIVE" : form.accountStatus}
                                onChange={(e) => setForm({ ...form, accountStatus: e.target.value })}
                            >
                                <option value="ACTIVE">Actif</option>
                                <option value="DEACTIVATED">Désactivé</option>
                            </select>
                        </label>
                    )}
                    <div className="profile-form-actions profile-form-actions--end">
                        <button type="submit" className="btn btn-primary btn-inline" disabled={saving}>
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary btn-inline"
                            onClick={() => (selected ? setViewMode("detail") : backToList())}
                        >
                            Annuler
                        </button>
                    </div>
                </form>
                {statusDialog}
            </section>
        );
    }

    if (viewMode === "detail" && selected) {
        return (
            <section className="card profile-section">
                <div className="section-header">
                    <div>
                        <BackLink onClick={backToList} className="purchase-detail-back">
                            Retour à la liste
                        </BackLink>
                        <h2>Détails de l&apos;employé</h2>
                    </div>
                    {buildMenuItems(selected, { includeView: false }).length > 0 && (
                        <RowActionsMenu
                            ariaLabel={`Actions pour ${displayName(selected)}`}
                            items={buildMenuItems(selected, { includeView: false })}
                        />
                    )}
                </div>
                <dl className="profile-detail-view profile-detail-view--compact">
                    <div className="profile-detail-row">
                        <dt>Nom</dt>
                        <dd>{displayName(selected)}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Nom d&apos;utilisateur</dt>
                        <dd>{selected.username}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Rôle</dt>
                        <dd>{STAFF_ROLE_LABELS[selected.role] || selected.role}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>Statut</dt>
                        <dd>{formatAccountStatus(selected.accountStatus)}</dd>
                    </div>
                    <div className="profile-detail-row">
                        <dt>E-mail</dt>
                        <dd>{selected.email || "—"}</dd>
                    </div>
                </dl>
                {!canMutate && (
                    <p className="muted">Consultation seule — seuls les administrateurs peuvent modifier les employés.</p>
                )}
                {canMutate && !selected.editable && (
                    <p className="muted">Les comptes administrateur sont consultables uniquement.</p>
                )}
                {statusDialog}
            </section>
        );
    }

    return (
        <section className="card profile-section">
            <div className="section-header">
                <div>
                    <h2>Gérer les employés</h2>
                    <p className="muted">
                        Personnel de l&apos;entreprise (administrateurs, managers, secrétaires).
                        {!canMutate && " Consultation seule."}
                    </p>
                </div>
                {canMutate && (
                    <button type="button" className="btn btn-primary" onClick={openCreate}>
                        Nouvel employé
                    </button>
                )}
            </div>
            {loading ? (
                <p className="muted">Chargement…</p>
            ) : employees.length === 0 ? (
                <p className="muted">Aucun employé.</p>
            ) : (
                <div className="admin-table">
                    {employees.map((employee) => (
                        <div key={employee.username} className="admin-row admin-list-row">
                            <button
                                type="button"
                                className="admin-list-row-main"
                                onClick={() => openDetail(employee)}
                            >
                                <div className="admin-row-body">
                                    <strong>{displayName(employee)}</strong>
                                    <p className="muted">
                                        {employee.username}
                                        {" · "}
                                        {STAFF_ROLE_LABELS[employee.role] || employee.role}
                                        {" · "}
                                        {formatAccountStatus(employee.accountStatus)}
                                    </p>
                                </div>
                            </button>
                            {buildMenuItems(employee).length > 0 && (
                                <RowActionsMenu
                                    ariaLabel={`Actions pour ${displayName(employee)}`}
                                    items={buildMenuItems(employee)}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
            {statusDialog}
        </section>
    );
}
